import { isEmpty } from "../../functions/alert";
import { isUnchanged } from "../../functions/edit";
import { invalidName } from "../../functions/general";

export const TASK_SECTION = {
  SETTINGS: "settings",
  QUESTION: "question",
  ANSWER: "answer",
  RECOMMENDATIONS: "recommendations",
  SOLUTION: "solution",
};

function settingsSlice(task) {
  return {
    title: task.title,
    identifier: task.identifier,
    domains: task.domains,
    tags: task.tags,
    noTranslationNeeded: task.noTranslationNeeded,
  };
}

function answerSlice(task) {
  return {
    answer_type: task.answer_type,
    answer_structure: task.answer_structure,
    answer_layout: task.answer_layout,
    answer_sorting: task.answer_sorting,
    task_answers: task.task_answers,
  };
}

function getAnswerTypeId(type) {
  if (type === "mcq") return 1;
  if (type === "input") return 2;
  return 0;
}

function getAnswerStructureId(type) {
  if (type === "default") return 1;
  if (type === "group") return 2;
  if (type === "sequence") return 3;
  if (type === "open") return 4;
  return 1;
}

function getAnswerSortingId(type) {
  return type === "random" ? 2 : 1;
}

function getAnswerLayoutId(type) {
  return type === "horizontal" ? 2 : 1;
}

/**
 * @param {object | null | undefined} original
 * @param {object} task
 * @param {string} section One of TASK_SECTION.*
 */
export function isTaskSectionDirty(original, task, section) {
  if (!original || !task) return false;
  switch (section) {
    case TASK_SECTION.SETTINGS:
      return !isUnchanged(settingsSlice(original), settingsSlice(task));
    case TASK_SECTION.QUESTION:
      return !isUnchanged(original.languages, task.languages);
    case TASK_SECTION.ANSWER:
      return !isUnchanged(answerSlice(original), answerSlice(task));
    case TASK_SECTION.RECOMMENDATIONS:
      return !isUnchanged(
        original.grade_difficulty,
        task.grade_difficulty
      );
    case TASK_SECTION.SOLUTION:
      return !isUnchanged(
        original.solutions ?? "",
        task.solutions ?? ""
      );
    default:
      return false;
  }
}

export function getTaskSectionValidationError(task, section) {
  switch (section) {
    case TASK_SECTION.SETTINGS:
      if (invalidName(task.title)) return "Invalid task title.";
      if (invalidName(task.identifier)) return "Invalid task identifier.";
      return null;
    case TASK_SECTION.QUESTION: {
      const content = task.languages?.[0]?.content;
      if (isEmpty([content])) return "Task content cannot be empty.";
      return null;
    }
    case TASK_SECTION.ANSWER: {
      const type = task.answer_type;
      if (type === "mcq") {
        const choices = task.task_answers ?? [];
        if (choices.length === 0) return "MCQ must have at least one choice.";
        if (!choices.some((c) => c.correct))
          return "Select exactly one correct answer for MCQ.";
      } else if (type === "input") {
        const rows = task.task_answers ?? [];
        if (!String(rows[0]?.task_labels?.[0]?.content ?? "").trim())
          return "Answer label is required for input type.";
        if (rows.some((a) => (a.answer ?? "").trim() === ""))
          return "Fill in the correct answer for each input field.";
      }
      return null;
    }
    case TASK_SECTION.RECOMMENDATIONS:
      return null;
    case TASK_SECTION.SOLUTION:
      return null;
    default:
      return null;
  }
}

export function buildPatchTaskSettingsPayload(task) {
  const tag_id = [...new Set([...(task.domains ?? []), ...(task.tags ?? [])])];
  return {
    id: task.id,
    title: task.title ?? "",
    identifier: task.identifier ?? "",
    tag_id,
    noTranslationNeeded: Boolean(task.noTranslationNeeded),
  };
}

export function buildPatchTaskSolutionPayload(task) {
  return {
    id: task.id,
    solutions: task.solutions ?? "",
  };
}

export function buildPatchTaskContentPayload(original, task, remoderate = 0) {
  const payload = {
    id: task.id,
    taskContents: [],
    "re-moderate": Number(remoderate),
  };
  const languages = task.languages ?? [];
  for (const language of languages) {
    const originalLanguage = original.languages?.find((l) => l.id === language.id);
    if (JSON.stringify(language) !== JSON.stringify(originalLanguage)) {
      payload.taskContents.push({
        lang_id: language.id,
        title: language.task_title,
        content: language.content,
      });
    }
  }
  return payload;
}

export function buildPatchTaskRecommendationsPayload(task) {
  return {
    id: task.id,
    recommended_grade: (task.grade_difficulty ?? []).map((r) => r.grade),
    recommended_difficulty: (task.grade_difficulty ?? []).map(
      (r) => r.difficulty
    ),
  };
}

export function buildPatchTaskAnswersPayload(task) {
  const answers = [];
  (task.task_answers ?? []).forEach((el) => {
    if (el.task_labels?.length > 0) {
      answers.push({
        answer_id: el.id,
        answer: el.answer,
        label: el.task_labels[0].content,
        label_id: el.task_labels[0].id,
      });
    }
  });
  return {
    id: task.id,
    answer_type: getAnswerTypeId(task.answer_type),
    answer_structure: getAnswerStructureId(task.answer_structure),
    answer_sorting: getAnswerSortingId(task.answer_sorting),
    answer_layout: getAnswerLayoutId(task.answer_layout),
    answers,
  };
}
